import { ExportError, errorMessage } from './errors';
import { log } from './logger';

/**
 * **Getting a file off the phone.** BAK-1's *"via Web Share or download"*, and nothing else.
 *
 * Two routes, tried in order, and **both fail loudly**. A backup is the mitigation the whole design
 * leans on for storage eviction and device loss (§13, P4-1); an export that silently did nothing is
 * worse than no export at all, because the user believes they have a copy.
 *
 * ENV-4: nothing here posts anywhere. Both routes hand the bytes to the operating system — the share
 * sheet, or the browser's own download path.
 *
 * ── Why this is not `harness/shareOut.ts` ────────────────────────────────────
 *
 * That module shares a *JSON string plus a text summary* and lives in the measurement harness, which
 * is deleted when U-0 and U-1's assumptions are settled. This one shares **bytes** — a zip now
 * (BAK-1), a PNG later (CARD-2) — and belongs to the product. The overlap is the two-route ladder
 * and the loud failure, roughly a dozen lines; making the harness's payload type generic to share
 * them would couple the product's only backup path to a module scheduled for deletion.
 */

const MODULE = 'backup';

export type ShareRoute = 'share-file' | 'download';

export interface ShareableFile {
  readonly bytes: Uint8Array;
  readonly filename: string;
  readonly mime: string;
  /** Shown on the iOS share sheet. */
  readonly title: string;
  /**
   * What this file **is**, in the user's words, for the messages below — *"backup"*, *"card"*.
   *
   * Required rather than defaulted, and added by build unit U-15. Every message in this module was
   * written for BAK-1 and said *backup* verbatim, so cancelling a **share card** at a touchline
   * produced *"Share cancelled. The backup was not saved anywhere."* — a sentence that names the
   * wrong artifact and implies data loss where there is none. A default would have kept that
   * failure one call site away; making it required means the next payload this module carries has
   * to answer the question before it can compile.
   */
  readonly subject: string;
}

/**
 * An intersection rather than an `extends`: `lib.dom` declares `canShare`/`share` as **required**
 * on `Navigator`, and iOS Safari 17 does not always have them. Widening them to optional through
 * inheritance is a type error; intersecting narrows to the same effect and keeps the check honest.
 */
type ShareCapableNavigator = Navigator & {
  canShare?: (data: ShareData) => boolean;
  share?: (data: ShareData) => Promise<void>;
};

function toFile(file: ShareableFile): File {
  // `slice()` detaches the view from whatever buffer produced it: `Uint8Array` over a larger
  // `ArrayBuffer` would otherwise share its bytes, and the archive builder reuses buffers.
  return new File([file.bytes.slice().buffer as ArrayBuffer], file.filename, { type: file.mime });
}

/**
 * Shares the file, falling back to a download.
 *
 * A **cancelled share sheet is reported as a cancellation**, never converted into a download the
 * user did not ask for — a file appearing in Downloads after the user pressed Cancel is a surprise,
 * and the retry is one tap.
 *
 * @throws {ExportError} when the user cancels, or when every route failed. The message names what
 *   was tried, because at a venue there is no console to read.
 */
export async function shareFile(file: ShareableFile): Promise<ShareRoute> {
  const attempts: string[] = [];
  const nav = navigator as ShareCapableNavigator;

  if (typeof nav.share === 'function') {
    try {
      const payload = { files: [toFile(file)], title: file.title };
      if (!nav.canShare || nav.canShare(payload)) {
        await nav.share(payload);
        log.info(MODULE, 'shared a file', {
          subject: file.subject,
          filename: file.filename,
          bytes: file.bytes.length,
        });
        return 'share-file';
      }
      attempts.push('navigator.canShare rejected a file attachment');
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') {
        throw new ExportError(`Share cancelled. The ${file.subject} was not saved anywhere.`, {
          cause,
        });
      }
      attempts.push(`file share failed: ${errorMessage(cause)}`);
    }
  } else {
    attempts.push('navigator.share is unavailable');
  }

  try {
    downloadFile(file);
    return 'download';
  } catch (cause) {
    attempts.push(`download failed: ${errorMessage(cause)}`);
  }

  throw new ExportError(
    `Could not save the ${file.subject}. Tried: ${attempts.join('; ')}. Your data is still on the device — try again, or use a different browser.`,
  );
}

/** @throws {ExportError} if the object URL or the anchor click fails. */
export function downloadFile(file: ShareableFile): void {
  try {
    const blob = new Blob([file.bytes.slice().buffer as ArrayBuffer], { type: file.mime });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Revoked on a later task so Safari has issued the download before the URL disappears.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    log.info(MODULE, 'downloaded a file', {
      subject: file.subject,
      filename: file.filename,
      bytes: file.bytes.length,
    });
  } catch (cause) {
    throw new ExportError('Download failed.', { cause });
  }
}
